import React from 'react';
import { Zap, Menu, X, BookOpen, Calculator, LineChart, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDarkMode = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.classList.toggle('dark', newIsDark);
    localStorage.setItem('darkMode', newIsDark ? 'dark' : 'light');
  };

  // Function to handle smooth scrolling to a section
  const scrollToSection = (elementId: string) => {
    // If we're not on the homepage, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const headerOffset = 80;
          const offsetPosition = element.offsetTop - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
          
          // Dispatch calculator event after scrolling
          if (elementId === 'calculator') {
            setTimeout(() => {
              const event = new CustomEvent('calculatorNavigated');
              document.dispatchEvent(event);
            }, 500);
          }
        }
      }, 200);
      return;
    }

    const element = document.getElementById(elementId);
    if (element) {
      const headerOffset = 80;
      const offsetPosition = element.offsetTop - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      // For mobile, close the menu after clicking
      setMobileMenuOpen(false);
      
      // Special handling for the calculator section
      if (elementId === 'calculator') {
        setTimeout(() => {
          const event = new CustomEvent('calculatorNavigated');
          document.dispatchEvent(event);
        }, 500);
      }
    }
  };

  return (
    <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800 shadow-sm text-gray-900 dark:text-white py-3 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <div className="flex items-center bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl p-2 mr-3 shadow-lg shadow-blue-500/20">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold tracking-tight whitespace-nowrap bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">Stromkalkulator.no</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Oppdaterte strømpriser og kalkulatorer</p>
              </div>
            </Link>
          </div>
          
          {/* Desktop menu */}
          <nav className="hidden md:flex items-center space-x-2">
            <button
              onClick={toggleDarkMode}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isDark ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}
            >
              {isDark ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>
            <Link 
              to={location.pathname === '/' ? '#day-overview' : '/'}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('day-overview');
              }}
            >
              <LineChart className="h-4 w-4 mr-2" />
              <span>Strømpris</span>
            </Link>
            <Link 
              to={location.pathname === '/' ? '#calculator' : '/'}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('calculator');
              }}
            >
              <Calculator className="h-4 w-4 mr-2" />
              <span>Kalkulator</span>
            </Link>
            <Link 
              to="/blog"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              <span>Blogg</span>
            </Link>
            <Link 
              to="/norgespris"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center"
            >
              <Calculator className="h-4 w-4 mr-2" />
              <span>Norgespris</span>
            </Link>
            <Link 
              to="/sammenlign-leverandorer"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center"
            >
              <Calculator className="h-4 w-4 mr-2" />
              <span>Sammenlign</span>
            </Link>
          </nav>
          
          {/* Mobile menu toggle - increased touch target */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Lukk meny" : "Åpne meny"}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
        
        {/* Mobile menu - improved spacing */}
        {mobileMenuOpen && (
          <div className="mt-3 md:hidden border-t border-gray-200 dark:border-gray-800 pt-3 pb-2">
            <nav className="flex flex-col space-y-2">
              <button
                onClick={toggleDarkMode} 
                className="w-full text-left text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {isDark ? (
                  <>
                    <Sun className="h-4 w-4 mr-2" />
                    Bytt til lyst tema
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4 mr-2" />
                    Bytt til mørkt tema
                  </>
                )}
              </button>
              <Link 
                to={location.pathname === '/' ? '#day-overview' : '/'}
                className="w-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('day-overview');
                }}
              >
                <LineChart className="h-4 w-4 mr-3" />
                Strømpris
              </Link>
              <Link 
                to={location.pathname === '/' ? '#calculator' : '/'}
                className="w-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('calculator');
                }}
              >
                <Calculator className="h-4 w-4 mr-3" />
                Kalkulator
              </Link>
              <Link 
                to="/blog"
                className="w-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="h-4 w-4 mr-3" />
                Blogg
              </Link>
              <Link 
                to="/sammenlign-leverandorer"
                className="w-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calculator className="h-4 w-4 mr-3" />
                Sammenlign leverandører
              </Link>
              <Link 
                to="/norgespris"
                className="w-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium py-3 px-4 flex items-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calculator className="h-4 w-4 mr-3" />
                Norgespris
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;